import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { recognitionsAPI, usersAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Heart,
  Plus,
  Star,
  ThumbsUp,
  Award,
  Sparkles,
  Search,
} from 'lucide-react';

const kudosTypes = [
  { id: 'kudos', label: 'Kudos', icon: ThumbsUp, color: 'text-blue-500' },
  { id: 'star', label: 'Star Performer', icon: Star, color: 'text-amber-500' },
  { id: 'helpful', label: 'Helpful', icon: Heart, color: 'text-pink-500' },
  { id: 'achievement', label: 'Achievement', icon: Award, color: 'text-purple-500' },
];

export const Recognition = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [recognitions, setRecognitions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newRecognition, setNewRecognition] = useState({
    recipient_id: '',
    message: '',
    badge_type: 'kudos',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recognitionsRes, usersRes] = await Promise.all([
        recognitionsAPI.getAll(),
        usersAPI.search(),
      ]);
      setRecognitions(recognitionsRes.data);
      // Handle both array and object response formats
      const usersData = usersRes.data;
      setUsers(Array.isArray(usersData) ? usersData : (usersData.users || []));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchUsers = async (query) => {
    setSearchQuery(query);
    try {
      const response = await usersAPI.search({ search: query });
      // Handle both array and object response formats
      const usersData = response.data;
      setUsers(Array.isArray(usersData) ? usersData : (usersData.users || []));
    } catch (error) {
      console.error('Failed to search users:', error);
    }
  };

  const handleGiveRecognition = async (e) => {
    e.preventDefault();
    if (!newRecognition.recipient_id || !newRecognition.message.trim()) {
      toast.error('Please select a person and add a message');
      return;
    }
    setCreating(true);
    try {
      const response = await recognitionsAPI.create(newRecognition);
      setRecognitions([response.data, ...recognitions]);
      setNewRecognition({ recipient_id: '', message: '', badge_type: 'kudos' });
      setDialogOpen(false);
      toast.success('Recognition sent! +10 points awarded to recipient.');
    } catch (error) {
      toast.error('Failed to send recognition');
    } finally {
      setCreating(false);
    }
  };

  const getKudosType = (type) => kudosTypes.find(k => k.id === type) || kudosTypes[0];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="recognition-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Heart className="w-8 h-8 text-pink-500" />
              {t('recognition.title', 'Peer Recognition')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('recognition.subtitle', "Celebrate your colleagues' achievements")}
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full primary-gradient" data-testid="give-recognition-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('recognition.giveRecognition', 'Give Recognition')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t('recognition.giveRecognition', 'Give Recognition')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleGiveRecognition} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">{t('recognition.selectPerson', 'Select Person')}</label>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder={t('recognition.searchColleagues', 'Search colleagues...')}
                      value={searchQuery}
                      onChange={(e) => handleSearchUsers(e.target.value)}
                      className="pl-10"
                      data-testid="search-users-input"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1 border rounded-lg p-2">
                    {users.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setNewRecognition({ ...newRecognition, recipient_id: u.id })}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                          newRecognition.recipient_id === u.id
                            ? 'bg-primary/10 border border-primary/20'
                            : 'hover:bg-muted'
                        }`}
                        data-testid={`user-option-${u.id}`}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={u.avatar} />
                          <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{u.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{u.role?.replace('_', ' ')}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Recognition Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {kudosTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setNewRecognition({ ...newRecognition, badge_type: type.id })}
                        className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                          newRecognition.badge_type === type.id
                            ? 'border-primary bg-primary/5'
                            : 'hover:bg-muted'
                        }`}
                        data-testid={`kudos-type-${type.id}`}
                      >
                        <type.icon className={`w-5 h-5 ${type.color}`} />
                        <span className="text-sm font-medium">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Message</label>
                  <Textarea
                    placeholder="Share what they did great..."
                    value={newRecognition.message}
                    onChange={(e) => setNewRecognition({ ...newRecognition, message: e.target.value })}
                    rows={3}
                    data-testid="recognition-message-input"
                  />
                </div>

                <Button type="submit" className="w-full" disabled={creating} data-testid="submit-recognition-btn">
                  {creating ? 'Sending...' : 'Send Recognition'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Recognition Wall */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recognitions.length > 0 ? (
            recognitions.map((rec) => {
              const kudosType = getKudosType(rec.badge_type);
              return (
                <Card 
                  key={rec.id} 
                  className="card-hover overflow-hidden"
                  data-testid={`recognition-${rec.id}`}
                >
                  <div className={`h-2 ${
                    rec.badge_type === 'star' ? 'gold-gradient' :
                    rec.badge_type === 'helpful' ? 'bg-pink-500' :
                    rec.badge_type === 'achievement' ? 'bg-purple-500' :
                    'bg-blue-500'
                  }`} />
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-full ${
                        rec.badge_type === 'star' ? 'bg-amber-500/10' :
                        rec.badge_type === 'helpful' ? 'bg-pink-500/10' :
                        rec.badge_type === 'achievement' ? 'bg-purple-500/10' :
                        'bg-blue-500/10'
                      }`}>
                        <kudosType.icon className={`w-5 h-5 ${kudosType.color}`} />
                      </div>
                      <div className="flex-1">
                        <Badge variant="outline" className="mb-2">
                          {kudosType.label}
                        </Badge>
                        <p className="text-sm">{rec.message}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={rec.sender_avatar} />
                          <AvatarFallback>{rec.sender_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-xs text-muted-foreground">From</p>
                          <p className="text-sm font-medium">{rec.sender_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Avatar className="h-8 w-8 ml-auto">
                          <AvatarImage src={rec.recipient_avatar} />
                          <AvatarFallback>{rec.recipient_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-xs text-muted-foreground">To</p>
                          <p className="text-sm font-medium">{rec.recipient_name}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-center mt-3 text-xs text-muted-foreground">
                      <Sparkles className="w-3 h-3 mr-1 text-amber-500" />
                      +10 points awarded
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="col-span-full p-12 text-center">
              <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Recognitions Yet</h3>
              <p className="text-muted-foreground mb-4">
                Be the first to recognize a colleague!
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                Give Recognition
              </Button>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Recognition;
